import { useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import { memo, useState } from 'react';
import BookingChecklistItemState from '../../booking/components/BookingChecklistItemState';
import BookingMessageGenerator from '../../booking/components/BookingMessageGenerator';
import CountryForm from '../../booking/components/CountryForm';
import useAlterBookingState from '../../booking/hooks/useAlterBookingState';
import LockboxCodeForm from '../../booking/components/LockboxCodeForm';
import GuestBookView from '../../booking/components/operation/GuestBookView';
import assert from '../../core/util/assert';
import { Booking } from '../../domain/booking';
import { checklistQuery } from '../../main/queries';
import useProperty from '../../property/hooks/useProperty';

interface Props {
    bookingId: number;
}

const CheckinChecklist = memo(({ bookingId }: Props) => {
    const { isModuleEnabled } = useProperty();
    const { loading, error, data } = useQuery<{ booking: Booking }>(
        checklistQuery,
        {
            variables: { id: bookingId },
        }
    );
    const [alterBookingState] = useAlterBookingState(bookingId);
    const [governmentImportDialogOpen, setGovernmentImportDialogOpen] =
        useState(false);
    const [messageDialogOpen, setMessageDialogOpen] = useState(false);
    const [lockboxDialogOpen, setLockboxDialogOpen] = useState(false);

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error :(</p>;
    assert(data);
    const { booking } = data;

    if (
        booking.importedToGovernmentPortal &&
        booking.detailsRequested &&
        booking.detailsRecorded &&
        booking.hasInstructions &&
        booking.accommodationReady
    ) {
        return null;
    }

    return (
        <Box>
            <Typography variant="h5" component="h2">
                Checkin checklist
            </Typography>
            <FormGroup>
                {!booking.countryCode && (
                    <CountryForm
                        contactPhone={booking.contactPhone}
                        bookingId={booking.id}
                        value={booking.countryCode}
                    />
                )}
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!booking.lockboxCode}
                                color="success"
                            />
                        }
                        label={'Lockbox code'}
                        onClick={() => {
                            if (!booking.lockboxCode) {
                                setLockboxDialogOpen(true);
                            }
                        }}
                    />
                    {lockboxDialogOpen && (
                        <LockboxCodeForm
                            bookingId={booking.id}
                            onClose={() => {
                                setLockboxDialogOpen(false);
                            }}
                        />
                    )}
                </Box>
                {isModuleEnabled('data-reporting/hungary') && (
                    <>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={booking.importedToGovernmentPortal}
                                    color="success"
                                    onChange={() => {
                                        if (
                                            !booking.importedToGovernmentPortal
                                        ) {
                                            setGovernmentImportDialogOpen(true);
                                        } else {
                                            alterBookingState({
                                                variables: {
                                                    id: booking.id,
                                                    fields: {
                                                        importedToGovernmentPortal:
                                                            false,
                                                    },
                                                },
                                                optimisticResponse: {
                                                    alterBookingState: {
                                                        __typename: 'Booking',
                                                        id: booking.id,
                                                        reference:
                                                            booking.reference,
                                                        importedToGovernmentPortal:
                                                            false,
                                                        detailsRequested:
                                                            booking.detailsRequested,
                                                        detailsRecorded:
                                                            booking.detailsRecorded,
                                                        hasInstructions:
                                                            booking.hasInstructions,
                                                        accommodationReady:
                                                            booking.accommodationReady,
                                                        accommodationVerified:
                                                            booking.accommodationVerified,
                                                        keysSecured:
                                                            booking.keysSecured,
                                                        hasReview:
                                                            booking.hasReview,
                                                    },
                                                },
                                            });
                                        }
                                    }}
                                />
                            }
                            label={'Imported booking to government portal'}
                        />
                        {governmentImportDialogOpen && (
                            <GuestBookView
                                bookingId={booking.id}
                                onClose={() => {
                                    setGovernmentImportDialogOpen(false);
                                }}
                            />
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={booking.detailsRequested}
                                    color="success"
                                    onChange={() => {
                                        if (!booking.detailsRequested) {
                                            setMessageDialogOpen(true);
                                        } else {
                                            alterBookingState({
                                                variables: {
                                                    id: booking.id,
                                                    fields: {
                                                        detailsRequested:
                                                            false,
                                                    },
                                                },
                                                optimisticResponse: {
                                                    alterBookingState: {
                                                        __typename: 'Booking',
                                                        id: booking.id,
                                                        reference:
                                                            booking.reference,
                                                        importedToGovernmentPortal:
                                                            booking.importedToGovernmentPortal,
                                                        detailsRequested:
                                                            false,
                                                        detailsRecorded:
                                                            booking.detailsRecorded,
                                                        hasInstructions:
                                                            booking.hasInstructions,
                                                        accommodationReady:
                                                            booking.accommodationReady,
                                                        accommodationVerified:
                                                            booking.accommodationVerified,
                                                        keysSecured:
                                                            booking.keysSecured,
                                                        hasReview:
                                                            booking.hasReview,
                                                    },
                                                },
                                            });
                                        }
                                    }}
                                />
                            }
                            label={'Guest details requested'}
                        />
                        {messageDialogOpen && (
                            <BookingMessageGenerator
                                bookingId={bookingId}
                                onClose={() => {
                                    setMessageDialogOpen(false);
                                }}
                            />
                        )}
                        <BookingChecklistItemState
                            label={'Guest details recorded'}
                            booking={booking}
                            statusField="detailsRecorded"
                        />
                    </>
                )}
                <BookingChecklistItemState
                    label={'Instructions sent'}
                    booking={booking}
                    statusField="hasInstructions"
                />
                <BookingChecklistItemState
                    label={'Accommodation ready'}
                    booking={booking}
                    statusField="accommodationReady"
                />
            </FormGroup>
        </Box>
    );
});

export default CheckinChecklist;
